/*
  <animated-image> — pause/play control for animated GIFs.

  Accessibility — WCAG 2.2.2 Pause, Stop, Hide / SortSite F7
  ("Avoid animated images over 5 seconds long that can't be paused or stopped",
  flagged on /pages/making-our-gummies for the process-step GIFs).

  A GIF cannot be paused through any media API, so "pause" is implemented by
  painting the frame that is currently on screen into a <canvas> and layering
  that canvas over the still-running <img>. Overlaying rather than replacing is
  deliberate: the <img> stays in the DOM and in the accessibility tree, so its
  alt text is unaffected, and resuming is a single node removal with no network
  request and no restart of the animation.

  The canvas is cross-origin-tainted (Shopify CDN), which is fine — drawImage
  and rendering are permitted; only pixel readback would be blocked, and we
  never read back.

  Users with `prefers-reduced-motion: reduce` get the paused state on load, and
  can still press play if they want the animation.

  SMP #483 (2026-09-29): the control is now a transparent full-image <button>
  (click the GIF to pause/play) instead of a visible corner disc.
*/
(function () {
  if (window.customElements && customElements.get('animated-image')) return;

  class AnimatedImage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.image = this.querySelector('img');
      this.button = this.querySelector('.animated-image__toggle');
      if (!this.image || !this.button) return;

      this.frame = null;
      this.still = null;
      this.stillReady = this.loadStill();

      // The control is authored hidden so that it never appears without the
      // script that makes it work.
      this.button.hidden = false;
      this.button.addEventListener('click', this.toggle.bind(this));

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.whenDecoded(() => this.stillReady.then(() => this.pause()));
      }
    }

    // Paused still (SMP #483), from the section's "Paused image" setting. Resolves either way; this.still is only
    // set when the asset actually loaded, so a missing file falls back to the
    // canvas snapshot instead of pausing on a broken image.
    loadStill() {
      const url = this.dataset.still;
      if (!url) return Promise.resolve();
      return new Promise((resolve) => {
        const still = new Image();
        still.onload = () => {
          still.className = 'animated-image__frame';
          still.alt = '';
          still.setAttribute('aria-hidden', 'true');
          still.decoding = 'async';
          this.still = still;
          resolve();
        };
        still.onerror = () => resolve();
        still.src = url;
      });
    }

    whenDecoded(callback) {
      if (this.image.complete && this.image.naturalWidth) {
        callback();
      } else {
        this.image.addEventListener('load', callback, { once: true });
      }
    }

    get paused() {
      return this.frame !== null;
    }

    toggle() {
      if (this.paused) {
        this.play();
      } else {
        this.pause();
      }
    }

    pause() {
      if (this.paused) return;

      if (this.still) {
        this.appendChild(this.still);
        this.frame = this.still;
        this.setAttribute('data-paused', '');
        this.setLabel(this.button.dataset.playLabel);
        return;
      }

      // Size the frame from what is on screen, in device pixels, not from
      // naturalWidth. Shopify never upscales a GIF, so every `width=` srcset
      // candidate serves the same small original while claiming e.g. 1000w;
      // the browser then reports a shrunken naturalWidth (89px for a 204px
      // file) and a frame drawn at that size looks soft next to the running
      // GIF. drawImage reads the full decoded bitmap regardless.
      const naturalWidth = this.image.naturalWidth;
      const naturalHeight = this.image.naturalHeight;
      if (!naturalWidth || !naturalHeight) return;

      const ratio = window.devicePixelRatio || 1;
      const renderedWidth = this.image.getBoundingClientRect().width;
      const width = Math.max(naturalWidth, Math.round(renderedWidth * ratio));
      const height = Math.round(width * (naturalHeight / naturalWidth));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.className = 'animated-image__frame';
      canvas.setAttribute('aria-hidden', 'true');

      try {
        canvas.getContext('2d').drawImage(this.image, 0, 0, width, height);
      } catch (error) {
        // Nothing was painted, so leaving the animation running is better than
        // covering it with an empty canvas.
        return;
      }

      this.appendChild(canvas);
      this.frame = canvas;
      this.setAttribute('data-paused', '');
      this.setLabel(this.button.dataset.playLabel);
    }

    play() {
      if (!this.paused) return;

      this.frame.remove();
      this.frame = null;
      this.removeAttribute('data-paused');
      this.setLabel(this.button.dataset.pauseLabel);
    }

    // The toggle is the full-bleed overlay button (SMP #483); its accessible
    // name carries the state, so it is the only thing to update.
    setLabel(text) {
      if (!text) return;
      this.button.setAttribute('aria-label', text);
    }
  }

  customElements.define('animated-image', AnimatedImage);
})();
